import { getUserSettings } from '@surfline/web-common';
import { Speed } from 'types/units';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useAppSelector } from '../../stores/hooks';
import useGetWindStationsInBoundingBoxQuery from '../../actions/windStations';

import useMapLocation from './useMapLocation';
import useSyncValueToStore from '../useSyncValueToStore';

import { setMapError, setMapLoading, setMapMessage } from '../../actions/mapV2';

const useWindStationsBoundingBoxQuery = () => {
  const { boundingBox } = useMapLocation();
  const settings = useAppSelector(getUserSettings);

  const queryArg = boundingBox ? { ...boundingBox, units: settings?.units } : skipToken;

  const { data, error, isFetching, isUninitialized } = useGetWindStationsInBoundingBoxQuery(
    queryArg,
    {
      // No longer need skip option here if queryArg is skipToken
      // However, keeping it doesn't harm and might be slightly clearer for future readers
      // or if queryArg wasn't skipToken but just undefined for some reason.
      // For consistency with original and explicit control, let's keep it for now if not redundant.
      // RTK Query docs suggest if arg is skipToken, query is skipped, making options.skip redundant.
      // Let's remove options.skip for cleanliness if skipToken is used for the arg.
    },
  );

  const loading = isFetching || isUninitialized;

  const windStationData = data?.data;

  const windSpeedUnits = data?.associated?.units?.windSpeed as Speed;

  const hasWindStations = windStationData && windStationData?.length > 0;

  const noWindStationsFound = !hasWindStations && !error && !loading;

  // If we want a message to display when no wind stations are found update this to something like:
  // const message = noWindStationsFound
  //   ? 'No wind stations found! Try zooming out or moving map'
  //   : '';
  const message = undefined;

  useSyncValueToStore(setMapError, error);
  useSyncValueToStore(setMapLoading, loading);
  useSyncValueToStore(setMapMessage, message);

  return {
    windStations: windStationData,
    windSpeedUnits,
    loading,
    error,
    noWindStationsFound,
    hasWindStations,
  };
};

export default useWindStationsBoundingBoxQuery;
